import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, Loader, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase/client';
import api from '../utils/firebase/api';
import { serviceTicketService } from '../utils/services/serviceTicketService';

interface InvoiceUploadDialogProps {
    open: boolean;
    onClose: () => void;
    currentUser: { id: string; name: string; role: string; companyId?: string };
    onSuccess: () => void;
}

export function InvoiceUploadDialog({ open, onClose, currentUser, onSuccess }: InvoiceUploadDialogProps) {
    const [uploading, setUploading] = useState(false);
    const [tickets, setTickets] = useState<any[]>([]);

    // Form State
    const [file, setFile] = useState<File | null>(null);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [amount, setAmount] = useState('');
    const [selectedTicketId, setSelectedTicketId] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (open) {
            loadTickets();
        }
    }, [open]);

    const loadTickets = async () => {
        try {
            // Fetch open tickets for this vendor
            // getServiceTickets uses tenant context automatically
            const allTickets = await serviceTicketService.getServiceTickets({
                assignedTo: currentUser.role === 'vendor' ? currentUser.id : undefined
            });

            const vendorTickets = allTickets.filter(t =>
                (t.status === 'assigned' || t.status === 'in_progress' || t.status === 'resolved')
            );

            setTickets(vendorTickets);
        } catch (error) {
            console.error('Error loading tickets:', error);
            toast.error('Failed to load your service tickets');
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
                toast.error('File size must be less than 10MB');
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.error('Please select a file');
            return;
        }
        if (!invoiceNumber) {
            toast.error('Please enter an invoice number');
            return;
        }
        if (!amount) {
            toast.error('Please enter an amount');
            return;
        }

        try {
            setUploading(true);
            const companyId = currentUser.companyId || 'default';

            // 1. Upload File to Storage
            // Path: companies/{slug}/invoices/{filename}
            const timestamp = Date.now();
            const storagePath = `companies/${companyId}/invoices/${timestamp}_${file.name}`;
            const storageRef = ref(storage, storagePath);

            const snapshot = await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(snapshot.ref);

            // 2. Create Invoice Record in Firestore
            const invoiceData = {
                invoiceNumber,
                amount: parseFloat(amount),
                notes,
                fileUrl: downloadUrl,
                storagePath,
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,

                // Metadata
                companyId,
                uploadedBy: currentUser.name,
                uploadedById: currentUser.id,
                vendorId: currentUser.id, // Assuming current user is the vendor
                vendorName: currentUser.name,
                status: 'pending', // Default status
                approvalStatus: 'pending',
                paymentStatus: 'unpaid',

                // Link to Ticket
                ticketId: selectedTicketId || null,
                ticketNumber: tickets.find(t => t.id === selectedTicketId)?.ticketNumber || null,

                createdAt: new Date().toISOString(),
                uploadedAt: new Date().toISOString(),
            };

            await api.createInvoice(companyId, invoiceData);

            toast.success('Invoice uploaded successfully');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('Failed to upload invoice. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Upload Invoice</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* File Upload Area */}
                    <div className="grid w-full max-w-sm items-center gap-1.5">
                        <Label htmlFor="invoice-file">Invoice File</Label>
                        <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-slate-50 transition cursor-pointer relative">
                            <input
                                id="invoice-file"
                                type="file"
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                accept=".pdf,.jpg,.png,.jpeg"
                                onChange={handleFileChange}
                            />
                            {file ? (
                                <div className="flex items-center justify-center gap-2 text-blue-600">
                                    <FileText className="w-5 h-5" />
                                    <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
                                    <button onClick={(e) => { e.preventDefault(); setFile(null); }} className="z-10 text-slate-400 hover:text-red-500">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="text-slate-500">
                                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                                    <p className="text-sm font-medium">Click or Drag to Upload</p>
                                    <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG (Max 10MB)</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="invoice-number">Invoice #</Label>
                            <Input
                                id="invoice-number"
                                placeholder="INV-001"
                                value={invoiceNumber}
                                onChange={(e) => setInvoiceNumber(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="amount">Amount ($)</Label>
                            <Input
                                id="amount"
                                type="number"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Link to Ticket (Optional)</Label>
                        <Select value={selectedTicketId} onValueChange={setSelectedTicketId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a service ticket..." />
                            </SelectTrigger>
                            <SelectContent>
                                {tickets.map(ticket => (
                                    <SelectItem key={ticket.id} value={ticket.id}>
                                        #{ticket.ticketNumber} - {ticket.wellName} ({ticket.status})
                                    </SelectItem>
                                ))}
                                {tickets.length === 0 && (
                                    <div className="p-2 text-sm text-slate-500 text-center">No active tickets found</div>
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="notes">Notes</Label>
                        <Textarea
                            id="notes"
                            placeholder="Description of services..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={uploading || !file}>
                        {uploading ? (
                            <>
                                <Loader className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            'Upload Invoice'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
